import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { useT } from '@/i18n/labels';
import { conversationHref } from '@/utils/contactHistory';

interface ContinuesLinkProps {
  /** The conversation row's wire `previousSessionId` - the closed conversation the SERVER linked this one to (D7). */
  previousSessionId: string | null | undefined;
  testId: string;
}

/**
 * "Continues an earlier conversation" - the one-click way from a returning customer's new conversation back to the
 * closed one the server linked it to (ADR-033 D7: a closed conversation is terminal, there is no Reopen, so this link is
 * how the thread of a customer's problem stays whole).
 *
 * <b>It renders only what the server stamped.</b> It is given the row's `previousSessionId` and nothing else: no customer
 * id, no "most recent closed conversation" lookup. A blank / absent id renders nothing at all.
 *
 * <b>Following it is an ordinary row open.</b> It navigates to `/conversations/<id>` (keeping the agent's `?folder=`), and
 * the conversations page does the rest: the row is read through the same single-row door as any other, so the server's
 * visibility rule still applies - an earlier conversation this viewer may not see answers 404 and the page shows its
 * "not available" sentence. The link never opens a row itself and never carries the earlier conversation's content.
 */
export const ContinuesLink: React.FC<ContinuesLinkProps> = ({ previousSessionId, testId }) => {
  const target = typeof previousSessionId === 'string' ? previousSessionId.trim() : '';
  // Split in two so the router hooks run ONLY when there is a link to draw: the chat header renders this for every
  // conversation, and a header that needed a router merely to render nothing would be a needless coupling.
  return target ? <ContinuesLinkButton target={target} testId={testId} /> : null;
};

const ContinuesLinkButton: React.FC<{ target: string; testId: string }> = ({ target, testId }) => {
  const { t } = useT();
  const navigate = useNavigate();
  const { search } = useLocation();
  return (
    <button
      type="button"
      className="ac-continues-link"
      onClick={() => navigate(conversationHref(target, search))}
      data-testid={testId}
    >
      <Icon name="reply" size={13} />
      <span>{t('cp_continues')}</span>
    </button>
  );
};
